import { useEffect } from "react";
import { WorkspaceType } from "@shared/types";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import { useDispatch, useSelector } from "react-redux";
import { selectWorkspaceInitialized } from "@shared/slices/common.slice";
import { sharedLib } from "@shared/services/shared.service";

interface useInitializeWorkspaceProps {
  localStorageWorkspace?: string | null;
  allowAdminWorkspace?: boolean;
  isQuickConnectPath?: boolean;
}

const useSharedInitializeWorkspace = ({
  localStorageWorkspace,
  allowAdminWorkspace,
  isQuickConnectPath,
}: useInitializeWorkspaceProps) => {
  const dispatch = useDispatch();
  const workspaceInitialized = useSelector(selectWorkspaceInitialized);

  const { realUser, adminIsLimitedAccess, isWorkspaceValid, setWorkspaceType } =
    useSharedWorkspace({ allowAdminWorkspace });

  const setWorkspaceInitialized = (initialized: boolean) => {
    dispatch(
      sharedLib.slices.common.actions.setWorkspaceInitialized(initialized)
    );
  };

  useEffect(() => {
    if (!realUser) {
      setWorkspaceInitialized(false);
    }
  }, [realUser]);

  useEffect(() => {
    if (!realUser || workspaceInitialized) return;
    let workspaceType = getDefaultWorkspace();
    if (isQuickConnectPath) {
      workspaceType = WorkspaceType.USER;
    } else if (isWorkspaceValid(localStorageWorkspace as WorkspaceType)) {
      workspaceType = localStorageWorkspace as WorkspaceType;
    }
    setWorkspaceType(workspaceType);
    setWorkspaceInitialized(true);
  }, [realUser, adminIsLimitedAccess, workspaceInitialized]);

  const getDefaultWorkspace = () => {
    if (adminIsLimitedAccess && allowAdminWorkspace) return WorkspaceType.ADMIN;
    if (!realUser?.has_clients_connected && !realUser?.is_onboarding)
      return WorkspaceType.DEMO;
    return WorkspaceType.USER;
  };
};

export default useSharedInitializeWorkspace;
